import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "./ui/dropdown-menu";
import { 
  LayoutDashboard, 
  BookOpen, 
  Bell, 
  User, 
  LogOut, 
  Menu, 
  X,
  GraduationCap,
  Users,
  Settings,
  ChevronDown,
  Tag,
  CreditCard,
  Package,
  RotateCcw,
  Shield,
  Palette,
  TrendingUp,
  Users2,
  Award
} from "lucide-react";
import { toast } from "sonner";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'superadmin';

  const studentNav = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/courses', label: 'Courses', icon: BookOpen },
    { path: '/progress', label: 'My Progress', icon: TrendingUp },
    { path: '/certificates', label: 'Certificates', icon: GraduationCap },
    { path: '/subscription', label: 'Subscription', icon: CreditCard },
    { path: '/notifications', label: 'Notifications', icon: Bell, badge: unreadCount },
    { path: '/profile', label: 'Profile', icon: User }
  ];

  const adminNav = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/courses', label: 'Manage Courses', icon: BookOpen },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/bulk-enroll', label: 'Bulk Enroll', icon: Users2 },
    { path: '/admin/certificate-requests', label: 'Certificates', icon: Award },
    { path: '/admin/promo-codes', label: 'Promo Codes', icon: Tag },
    { path: '/admin/payments', label: 'Payments', icon: CreditCard },
    { path: '/admin/subscriptions', label: 'Subscriptions', icon: Package },
    { path: '/admin/refunds', label: 'Refunds', icon: RotateCcw },
    { path: '/admin/notifications', label: 'Notifications', icon: Bell }
  ];

  const superadminNav = [
    { path: '/superadmin', label: 'Dashboard', icon: Shield },
    { path: '/superadmin/customize', label: 'Customize', icon: Palette },
    { path: '/admin/courses', label: 'Manage Courses', icon: BookOpen },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/bulk-enroll', label: 'Bulk Enroll', icon: Users2 },
    { path: '/admin/certificate-requests', label: 'Certificates', icon: Award },
    { path: '/admin/promo-codes', label: 'Promo Codes', icon: Tag },
    { path: '/admin/payments', label: 'Payments', icon: CreditCard },
    { path: '/admin/subscriptions', label: 'Subscriptions', icon: Package },
    { path: '/admin/refunds', label: 'Refunds', icon: RotateCcw },
    { path: '/admin/notifications', label: 'Notifications', icon: Bell }
  ];

  const navItems = isSuperAdmin ? superadminNav : (isAdmin ? adminNav : studentNav);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, {
          withCredentials: true
        });
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  const isActive = (path) => {
    if (path === '/admin' || path === '/dashboard' || path === '/superadmin') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-violet-600 focus:text-white focus:rounded-lg"
      >
        Skip to main content
      </a>
      
      {/* Mobile Header */}
      <header className="sticky top-0 z-30 w-full bg-white/80 backdrop-blur-xl border-b border-stone-100 md:hidden" role="banner">
        <div className="px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
            aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </Button>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-violet-700" />
            <span className="font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>LearnHub</span>
          </div>
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-violet-100 text-violet-700">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-stone-100 z-40 flex flex-col p-6 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center gap-2 mb-8">
          <GraduationCap className="h-8 w-8 text-violet-700" aria-hidden="true" />
          <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>LearnHub</span>
        </div>

        <nav className="flex-1 space-y-2" aria-label="Primary">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              onClick={() => setSidebarOpen(false)}
              aria-current={isActive(item.path) ? 'page' : undefined}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-violet-50 text-violet-700 font-medium'
                  : 'text-stone-600 hover:bg-stone-50 hover:text-violet-700'
              }`}
            >
              <item.icon className="h-5 w-5" aria-hidden="true" />
              <span>{item.label}</span>
              {item.badge > 0 && (
                <span className="ml-auto bg-orange-500 text-white text-xs rounded-full px-2 py-0.5" aria-label={`${item.badge} unread notifications`}>
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="pt-4 border-t border-stone-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-4 py-3 h-auto" data-testid="user-menu-btn">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-violet-100 text-violet-700">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="font-medium text-stone-900 truncate">{user?.name}</p>
                  <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-stone-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main id="main-content" className="md:ml-64 min-h-screen" role="main" tabIndex="-1">
        <div className="p-6 md:p-12">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
